import { create } from 'zustand';
import {
  fetchStaffUsers,
  FrontendStaffUser,
  approveStaffAccess,
  rejectStaffAccess,
  setPendingStaffAccess
} from '../services/staff/staffService';

export type AccessRequestStatus = 'pending' | 'approved' | 'rejected' | null;

export interface StaffUser extends FrontendStaffUser {
  name?: string;
  access_request?: AccessRequestStatus;
  restaurant_role?: string | null;
}

interface StaffState {
  staff: StaffUser[];
  isLoading: boolean;
  error: string | null;
  isDataLoaded: boolean;

  // Actions
  load: () => Promise<void>;
  reload: () => Promise<void>;
  clear: () => void;
  approveStaff: (staffId: string) => Promise<void>;
  rejectStaff: (staffId: string) => Promise<void>;
  setPendingStaff: (staffId: string) => Promise<void>;
  updateStaffInStore: (staffId: string, updates: Partial<StaffUser>) => void;

  // Selectors
  getPendingCount: () => number;
  getStaffByStatus: (status: AccessRequestStatus | 'all') => StaffUser[];
}

export const useStaffStore = create<StaffState>((set, get) => ({
  staff: [],
  isLoading: false,
  error: null,
  isDataLoaded: false,

  load: async () => {
    const state = get();
    if (state.isLoading || state.isDataLoaded) return;

    set({ isLoading: true, error: null });

    try {
      const data = await fetchStaffUsers();
      set({
        staff: data as StaffUser[],
        isDataLoaded: true,
        isLoading: false
      });
    } catch (err: any) {
      console.error('Failed to load staff:', err);
      set({
        error: err.message || 'Failed to load staff',
        isLoading: false
      });
    }
  },

  reload: async () => {
    set({ isDataLoaded: false });
    await get().load();
  },

  clear: () => set({
    staff: [],
    isDataLoaded: false,
    error: null,
    isLoading: false
  }),

  approveStaff: async (staffId: string) => {
    await approveStaffAccess(staffId);
    get().updateStaffInStore(staffId, { access_request: 'approved' });
  },

  rejectStaff: async (staffId: string) => {
    await rejectStaffAccess(staffId);
    get().updateStaffInStore(staffId, { access_request: 'rejected' });
  },

  setPendingStaff: async (staffId: string) => {
    await setPendingStaffAccess(staffId);
    get().updateStaffInStore(staffId, { access_request: 'pending' });
  },

  updateStaffInStore: (staffId: string, updates: Partial<StaffUser>) => {
    set(state => ({
      staff: state.staff.map(s =>
        s.id === staffId ? { ...s, ...updates } : s
      )
    }));
  },

  getPendingCount: () => {
    return get().staff.filter(s => s.access_request === 'pending').length;
  },

  getStaffByStatus: (status: AccessRequestStatus | 'all') => {
    const staff = get().staff;
    if (status === 'all') return staff;
    return staff.filter(s => s.access_request === status);
  },
}));
